import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, Loader2 } from "lucide-react";
import { api } from "../api";
import StoryScene from "../StoryScene";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GOLD_TOAST = { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" };
const VISUALS = ["stamp", "delhi", "ingredients", "dish", "timeline", "quiz-intro"];

const Input = ({ value, onChange, testId, className }) => (
  <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
    className={`bg-[#1A1A1A] border border-white/10 rounded-xl px-3 py-2.5 text-xs focus:outline-none focus:border-[#D4AF37]/60 ${className || "w-full"}`} />
);

export const AdminStories = () => {
  const [scenes, setScenes] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [preview, setPreview] = useState(null);
  const [video, setVideo] = useState(null);
  const [uploading, setUploading] = useState(null);

  useEffect(() => {
    if (!edit?.slug) return;
    api.get(`/dishes/${edit.slug}`).then((r) => setVideo(r.data.story_video_url ? { url: r.data.story_video_url } : null)).catch(() => setVideo(null));
  }, [edit?.slug]);

  const uploadVideo = async (file) => {
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) return toast.error("Keep the clip under 60 MB");
    const CHUNK = 4 * 1024 * 1024;
    const total = Math.ceil(file.size / CHUNK);
    const uploadId = [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, "0")).join("");
    setUploading(0);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", i);
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "part.bin");
        await api.post(`/admin/story-video/${edit.slug}/chunk`, fd);
        setUploading(Math.round(((i + 1) / (total + 1)) * 100));
      }
      const r = await api.post(`/admin/story-video/${edit.slug}/finish`, { upload_id: uploadId, total_chunks: total, filename: file.name });
      setVideo({ url: `${r.data.video_url}?v=${Date.now()}` });
      toast.success("Clip is live — it loops at the top of this dish's story", { style: GOLD_TOAST });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed — please try again");
    }
    setUploading(null);
  };

  const removeVideo = async () => {
    try {
      await api.delete(`/admin/story-video/${edit.slug}`);
      setVideo(null);
      toast.success("Clip removed", { style: GOLD_TOAST });
    } catch {
      toast.error("Could not remove the clip");
    }
  };

  useEffect(() => {
    api.get("/admin/story-scenes").then((r) => {
      setScenes(r.data.scenes);
      if (r.data.scenes[0]) setEdit(JSON.parse(JSON.stringify(r.data.scenes[0])));
    }).catch(() => {});
  }, []);

  if (!edit) return <p className="text-xs text-white/40">Loading story studio…</p>;

  const pickScene = (slug) => {
    const sc = scenes.find((s) => s.slug === slug);
    if (sc) setEdit(JSON.parse(JSON.stringify(sc)));
  };

  const setSeg = (i, patch) => setEdit({ ...edit, segments: edit.segments.map((s, x) => (x === i ? { ...s, ...patch } : s)) });
  const setOpt = (i, v) => setEdit({ ...edit, question: { ...edit.question, options: edit.question.options.map((o, x) => (x === i ? v : o)) } });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/story-scene/${edit.slug}`, {
        enabled: edit.enabled,
        stamp_place: edit.stamp_place,
        stamp_year: edit.stamp_year,
        points: Number(edit.points),
        recommend: edit.recommend,
        segments: edit.segments.map((s) => ({ ...s, dur: Number(s.dur) })),
        question: { ...edit.question, answer: Number(edit.question.answer) },
        correct_line: edit.correct_line,
        wrong_line: edit.wrong_line,
      });
      setEdit(JSON.parse(JSON.stringify(r.data)));
      setScenes(scenes.map((s) => (s.slug === r.data.slug ? r.data : s)));
      toast.success("Story saved — edited narration re-records once on its next play", { style: GOLD_TOAST });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the story");
    }
    setSaving(false);
  };

  const openPreview = async () => {
    setLoadingPrev(true);
    try {
      const dish = (await api.get(`/dishes/${edit.slug}`)).data;
      let scene;
      try {
        scene = (await api.get(`/story-scene/${edit.slug}`, { params: { user_id: "admin-preview" } })).data;
      } catch {
        scene = { ...edit, question: { text: edit.question.text, options: edit.question.options }, recommend_dish: null, already_earned: false };
      }
      setPreview({ scene, dish });
    } catch {
      toast.error("Could not load the preview");
    }
    setLoadingPrev(false);
  };

  return (
    <div data-testid="admin-stories" className="space-y-5">
      <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
        <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">STORY STUDIO — "BRING THIS STORY TO LIFE"</p>
        <p className="text-xs text-white/50">
          The cinematic stories guests watch on dish pages: passport stamp, illustrated origin scene, ingredients, one challenge question and a points celebration.
          Edit any line below — the narration re-records automatically in the chosen narrator voice (small one-time cost per changed line).
          Timing (seconds) is used when narration is muted or unavailable.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="story-scene-picker">
        {(scenes || []).map((s) => (
          <button key={s.slug} data-testid={`story-pick-${s.slug}`} onClick={() => pickScene(s.slug)}
            className={`rounded-full px-4 py-2 text-[10px] font-bold tracking-widest border ${
              edit.slug === s.slug ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-[#1A1A1A] text-white/60 border-white/10"}`}>
            {s.title.toUpperCase()}{!s.enabled && " · OFF"}
          </button>
        ))}
      </div>

      <div className="rounded-xl bg-[#121212] border border-white/10 p-4 space-y-3" data-testid={`story-editor-${edit.slug}`}>
        <div className="flex items-center justify-between">
          <p className="text-sm font-black">{edit.title}</p>
          <div className="flex items-center gap-2">
            <button data-testid="story-admin-preview" onClick={openPreview}
              className="flex items-center gap-1.5 text-[9px] font-bold tracking-widest text-black bg-[#D4AF37] rounded-full px-4 py-2">
              {loadingPrev ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />} PREVIEW STORY
            </button>
            <button data-testid="story-admin-enabled" onClick={() => setEdit({ ...edit, enabled: !edit.enabled })}
              className={`text-[9px] font-bold tracking-widest rounded-full px-4 py-2 border ${edit.enabled ? "bg-[#D4AF37]/15 border-[#D4AF37] text-[#D4AF37]" : "border-white/20 text-white/50"}`}>
              {edit.enabled ? "LIVE FOR GUESTS" : "SWITCHED OFF"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-[9px] tracking-widest text-white/40 mb-1">STAMP PLACE</p>
            <Input testId="story-admin-place" value={edit.stamp_place} onChange={(v) => setEdit({ ...edit, stamp_place: v })} />
          </div>
          <div>
            <p className="text-[9px] tracking-widest text-white/40 mb-1">STAMP YEAR</p>
            <Input testId="story-admin-year" value={edit.stamp_year} onChange={(v) => setEdit({ ...edit, stamp_year: v })} />
          </div>
          <div>
            <p className="text-[9px] tracking-widest text-white/40 mb-1">POINTS FOR CORRECT ANSWER</p>
            <Input testId="story-admin-points" value={edit.points} onChange={(v) => setEdit({ ...edit, points: v.replace(/\D/g, "") })} />
          </div>
          <div>
            <p className="text-[9px] tracking-widest text-white/40 mb-1">RECOMMEND NEXT (DISH SLUG)</p>
            <Input testId="story-admin-recommend" value={edit.recommend || ""} onChange={(v) => setEdit({ ...edit, recommend: v })} />
          </div>
        </div>

        <p className="text-[10px] tracking-widest text-[#D4AF37] pt-2">STORY VIDEO — LOOPING COOKING CLIP</p>
        <div className="rounded-xl bg-[#1A1A1A] border border-white/10 p-3" data-testid={`story-video-admin-${edit.slug}`}>
          <p className="text-[10px] text-white/45 mb-2 leading-relaxed">
            A short muted clip (mp4, webm or mov — under 60 MB) that loops at the top of this dish's story section.
            Uploading a new one replaces the old.
          </p>
          {video?.url && (
            <video data-testid="story-video-admin-preview" src={`${BACKEND_URL}${video.url}`}
              className="w-full max-h-44 object-cover rounded-xl mb-2 border border-white/10" controls muted loop playsInline preload="metadata" />
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <label className={`cursor-pointer bg-[#D4AF37] text-black text-[9px] font-black tracking-widest rounded-full px-4 py-2 ${uploading != null ? "opacity-60" : ""}`}>
              {uploading != null ? `UPLOADING ${uploading}%` : video?.url ? "REPLACE CLIP" : "UPLOAD CLIP"}
              <input data-testid="story-video-file-input" type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden"
                disabled={uploading != null} onChange={(e) => { uploadVideo(e.target.files[0]); e.target.value = ""; }} />
            </label>
            {video?.url && uploading == null && (
              <button data-testid="story-video-remove" onClick={removeVideo}
                className="text-[9px] font-bold tracking-widest text-[#E25822] border border-[#E25822]/50 rounded-full px-4 py-2">REMOVE</button>
            )}
          </div>
        </div>

        <p className="text-[10px] tracking-widest text-[#D4AF37] pt-2">SCENES & NARRATION</p>
        {edit.segments.map((s, i) => (
          <div key={s.id} className="rounded-xl bg-[#1A1A1A] border border-white/10 p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[9px] tracking-widest text-white/40">SCENE {i + 1}</span>
              <select data-testid={`story-admin-visual-${i}`} value={s.visual} onChange={(e) => setSeg(i, { visual: e.target.value })}
                className="xp-lang rounded-lg px-2 py-1 text-[10px] font-bold">
                {VISUALS.map((v) => <option key={v} value={v}>{v.toUpperCase()}</option>)}
              </select>
              <span className="ml-auto flex items-center gap-1 text-[9px] text-white/40">
                <input data-testid={`story-admin-dur-${i}`} value={s.dur} onChange={(e) => setSeg(i, { dur: e.target.value.replace(/[^0-9.]/g, "") })}
                  className="w-12 bg-[#121212] border border-white/10 rounded-lg px-2 py-1 text-[10px] text-center" />
                SEC
              </span>
            </div>
            <textarea data-testid={`story-admin-text-${i}`} value={s.text} rows={2}
              onChange={(e) => setSeg(i, { text: e.target.value })}
              className="w-full bg-[#121212] border border-white/10 rounded-xl px-3 py-2.5 text-xs leading-relaxed focus:outline-none focus:border-[#D4AF37]/60" />
          </div>
        ))}

        <p className="text-[10px] tracking-widest text-[#D4AF37] pt-2">THE CHALLENGE QUESTION</p>
        <textarea data-testid="story-admin-question" value={edit.question.text} rows={2}
          onChange={(e) => setEdit({ ...edit, question: { ...edit.question, text: e.target.value } })}
          className="w-full bg-[#1A1A1A] border border-white/10 rounded-xl px-3 py-2.5 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
        {edit.question.options.map((o, i) => (
          <div key={i} className="flex items-center gap-2">
            <button data-testid={`story-admin-correct-${i}`} onClick={() => setEdit({ ...edit, question: { ...edit.question, answer: i } })}
              title="Mark as the correct answer"
              className={`shrink-0 w-7 h-7 rounded-full border text-[10px] font-black ${edit.question.answer === i ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/20 text-white/40"}`}>
              {edit.question.answer === i ? "✓" : String.fromCharCode(65 + i)}
            </button>
            <Input testId={`story-admin-option-${i}`} value={o} onChange={(v) => setOpt(i, v)} />
          </div>
        ))}

        <div className="grid grid-cols-1 gap-2 pt-2">
          <div>
            <p className="text-[9px] tracking-widest text-white/40 mb-1">SPOKEN REACTION — CORRECT ANSWER</p>
            <Input testId="story-admin-correct-line" value={edit.correct_line} onChange={(v) => setEdit({ ...edit, correct_line: v })} />
          </div>
          <div>
            <p className="text-[9px] tracking-widest text-white/40 mb-1">SPOKEN REACTION — WRONG ANSWER (ENCOURAGING)</p>
            <Input testId="story-admin-wrong-line" value={edit.wrong_line} onChange={(v) => setEdit({ ...edit, wrong_line: v })} />
          </div>
        </div>

        <button data-testid="story-admin-save" onClick={save} disabled={saving}
          className="w-full bg-[#D4AF37] disabled:opacity-60 text-black text-xs font-bold rounded-full py-3.5 tracking-widest">
          {saving ? "SAVING…" : "SAVE STORY"}
        </button>
      </div>

      {preview && <StoryScene slug={edit.slug} scene={preview.scene} dish={preview.dish} onClose={() => setPreview(null)} />}
    </div>
  );
};
